import {
  EnrichmentAttemptOutcome,
  EnrichmentFieldState,
  EnrichmentProvider,
  type EnrichmentRunSummary,
  type EnrichmentStatusEntry,
  type RefreshCheckedReason,
  type RefreshOutcomeDto,
} from '@factoryfixinc/ats-interfaces';

export type TracePanelDepth = 'full' | 'thin';
export type TracePanelNaming = 'generic' | 'named';
export type TracePanelVariant =
  | 'updated'
  | 'verified'
  | 'instant'
  | 'checked'
  | 'degraded'
  | 'hidden';
export type TracePanelIcon = 'check' | 'ai';

// The final handoff resolves both product seams. Keeping them here makes any
// future copy-only experiment a one-line change.
export const TRACE_PANEL_DEPTH: TracePanelDepth = 'full';
export const TRACE_PANEL_NAMING: TracePanelNaming = 'named';

export interface TracePanelRow {
  id: string;
  icon: TracePanelIcon;
  headline: string;
  subline: string;
  details?: string[];
}

export interface TracePanelFooter {
  headline: string;
  subline?: string;
  tone: 'positive' | 'neutral';
}

export interface TracePanelSource {
  provider: EnrichmentProvider;
  label: string;
  attempted: boolean;
  // True only when the source ran and errored; excluded from `attempted` and the local count.
  errored: boolean;
}

export interface TracePanelSourceSummary {
  count: number;
  sources: TracePanelSource[];
  tone: 'positive';
}

export interface TracePanelModel {
  variant: Exclude<TracePanelVariant, 'hidden'>;
  claim: string;
  rows: TracePanelRow[];
  sourceSummary?: TracePanelSourceSummary;
  footer: TracePanelFooter;
}

export type TracePanelLoadingRowState = 'checking' | 'done';

export interface TracePanelLoadingRow {
  id: 'phone' | 'email';
  icon: TracePanelIcon;
  headline: string;
  state: TracePanelLoadingRowState;
}

export interface TracePanelLoadingModel {
  subline: string;
  rows: TracePanelLoadingRow[];
}

interface TracePanelMapperOptions {
  depth?: TracePanelDepth;
  naming?: TracePanelNaming;
  relativeTime?: string;
  // Wire count from the outcome DTO; checked first, before the result and the local attempted count.
  outcomeSourcesChecked?: number;
}

type EnrichmentOutcomeName = RefreshOutcomeDto['outcome'] | null;
type FieldStates = EnrichmentStatusEntry['fields'] | undefined;
type ReceiptVariant = Exclude<TracePanelVariant, 'hidden' | 'instant'>;

const SOURCE_DEFINITIONS: Array<Pick<TracePanelSource, 'provider' | 'label'>> = [
  { provider: EnrichmentProvider.PDL, label: 'People Data Labs' },
  { provider: EnrichmentProvider.PIPL, label: 'Pipl' },
  { provider: EnrichmentProvider.FIBER, label: 'Fiber' },
  { provider: EnrichmentProvider.NEXXT, label: 'Nexxt' },
  { provider: EnrichmentProvider.TRESTLE, label: 'Trestle' },
  { provider: EnrichmentProvider.CLEAROUT, label: 'Clearout' },
];
const HOW_WE_DECIDED =
  'We compared the available information and used the most reliable contact info. ' +
  'Only changes that passed verification were saved.';

// A source is attempted if it appears in result.sources with a non-error outcome; an
// error outcome is its own `errored` state, muted like not-attempted but distinct in the
// tooltip. Without a result (legacy status with no run summary), every source falls back
// to attempted except Fiber, which never runs on the unlock trigger this panel renders for.
function sourceOutcome(
  provider: EnrichmentProvider,
  result: EnrichmentRunSummary | null,
): Pick<TracePanelSource, 'attempted' | 'errored'> {
  if (!result) return { attempted: provider !== EnrichmentProvider.FIBER, errored: false };
  const source = result.sources.find((candidate) => candidate.provider === provider);
  if (!source) return { attempted: false, errored: false };
  if (source.outcome === EnrichmentAttemptOutcome.ERROR) {
    return { attempted: false, errored: true };
  }
  return { attempted: true, errored: false };
}

function buildSources(result: EnrichmentRunSummary | null): TracePanelSource[] {
  return SOURCE_DEFINITIONS.map((source) => ({
    ...source,
    ...sourceOutcome(source.provider, result),
  }));
}

function resolveVariant(
  outcome: EnrichmentOutcomeName,
  reason: RefreshCheckedReason | undefined,
  fields: FieldStates,
): TracePanelVariant {
  if (!outcome || outcome === 'checking') return 'hidden';
  if (outcome === 'updated') return 'updated';
  if (reason === 'recency' || reason === 'engagement') return 'instant';
  if (reason === 'no_change') return bothContactsVerified(fields) ? 'verified' : 'checked';
  if (reason === 'degraded') return 'degraded';
  if (reason === 'no_match') return 'checked';
  return 'hidden';
}

// A kept contact only counts as verified when its own check confirmed it; an inconclusive
// check leaves the field unchanged, and the panel must not upgrade that to "confirmed".
export function bothContactsVerified(fields: FieldStates): boolean {
  return (
    fields?.email === EnrichmentFieldState.VERIFIED &&
    fields?.phone === EnrichmentFieldState.VERIFIED
  );
}

function withRelativeTime(copy: string, relativeTime?: string): string {
  return relativeTime ? `${copy} · ${relativeTime.replace(/ /g, '\u00a0')}` : copy;
}

function commitLine(fields: FieldStates, relativeTime?: string): string | undefined {
  if (!fields) return undefined;

  const stateLabel: Partial<Record<EnrichmentFieldState, string>> = {
    [EnrichmentFieldState.UPDATED]: 'updated',
    [EnrichmentFieldState.VERIFIED]: 'verified',
    [EnrichmentFieldState.UNCHANGED]: 'checked',
    [EnrichmentFieldState.REMOVED]: 'removed',
  };
  const labels = {
    phone: 'Phone',
    email: 'Email',
  } as const;
  const parts = (Object.keys(labels) as (keyof typeof labels)[])
    .map((field) => {
      const label = stateLabel[fields[field]];
      return label ? `${labels[field]} ${label}` : undefined;
    })
    .filter((part): part is string => !!part);

  return parts.length ? withRelativeTime(parts.join(' · '), relativeTime) : undefined;
}

function topicRows(variant: ReceiptVariant, depth: TracePanelDepth): TracePanelRow[] {
  const includeDetails = depth === 'full';

  if (variant === 'updated') {
    return [
      {
        id: 'phone',
        icon: 'check',
        headline: 'Phone',
        subline: 'Mobile number is active and matches this candidate',
        details: includeDetails
          ? ['Recent activity on this number', 'Kept as the primary number']
          : undefined,
      },
      {
        id: 'email',
        icon: 'check',
        headline: 'Email',
        subline: 'Previous email is no longer deliverable',
        details: includeDetails
          ? ['Found a newer personal email', 'New email passed deliverability checks']
          : undefined,
      },
      {
        id: 'how-we-decided',
        icon: 'ai',
        headline: 'How we decided',
        subline: HOW_WE_DECIDED,
      },
    ];
  }

  if (variant === 'verified') {
    return [
      {
        id: 'phone',
        icon: 'check',
        headline: 'Phone',
        subline: 'Number confirmed active and owned by this candidate',
        details: includeDetails ? ['Line type: mobile', 'Name matches this candidate'] : undefined,
      },
      {
        id: 'email',
        icon: 'check',
        headline: 'Email',
        subline: 'Address confirmed deliverable',
        details: includeDetails ? ['Accepts mail', 'No junk or disposable flags'] : undefined,
      },
      {
        id: 'how-we-decided',
        icon: 'ai',
        headline: 'How we decided',
        subline: HOW_WE_DECIDED,
      },
    ];
  }

  const rows: TracePanelRow[] = [
    {
      id: 'phone',
      icon: 'check',
      headline: 'Phone',
      subline: 'Reviewed, kept as-is',
    },
    {
      id: 'email',
      icon: 'check',
      headline: 'Email',
      subline: 'Reviewed, kept as-is',
    },
  ];

  if (variant !== 'degraded') {
    rows.push({
      id: 'how-we-decided',
      icon: 'ai',
      headline: 'How we decided',
      subline: HOW_WE_DECIDED,
    });
  }

  return rows;
}

function footerFor(
  variant: Exclude<TracePanelVariant, 'hidden'>,
  reason: RefreshCheckedReason | undefined,
  fields: FieldStates,
  relativeTime?: string,
): TracePanelFooter {
  if (variant === 'instant') {
    return {
      headline: 'Contact info ready',
      subline: withRelativeTime(
        reason === 'recency'
          ? 'We recently checked this contact info'
          : 'Confirmed by recent activity in FactoryFix',
        relativeTime,
      ),
      tone: 'positive',
    };
  }
  if (variant === 'checked') {
    return {
      headline: 'Contact info unchanged',
      subline: withRelativeTime("We checked, but didn't find anything newer", relativeTime),
      tone: 'neutral',
    };
  }
  if (variant === 'degraded') {
    return {
      headline: 'Check incomplete',
      subline: withRelativeTime(
        "We couldn't complete the check. Existing contact info was left unchanged",
        relativeTime,
      ),
      tone: 'neutral',
    };
  }

  return {
    headline: variant === 'updated' ? 'Contact info updated' : 'Contact info verified',
    subline:
      commitLine(fields, relativeTime) ??
      withRelativeTime('Freshness check complete', relativeTime),
    tone: 'positive',
  };
}

export function mapEnrichmentTracePanel(
  outcome: EnrichmentOutcomeName,
  reason: RefreshCheckedReason | undefined,
  fields: FieldStates,
  // Drives per-source attempted state; its checked count is used only after the outcome's own count.
  result: EnrichmentRunSummary | null,
  options: TracePanelMapperOptions = {},
): TracePanelModel | null {
  const variant = resolveVariant(outcome, reason, fields);
  if (variant === 'hidden') return null;

  if (variant === 'instant') {
    return {
      variant,
      claim: 'No check needed on this unlock',
      rows: [],
      footer: footerFor(variant, reason, fields, options.relativeTime),
    };
  }

  const naming = options.naming ?? TRACE_PANEL_NAMING;
  const sources = buildSources(result);

  return {
    variant,
    claim: 'Ran automatically on unlock',
    rows: topicRows(variant, options.depth ?? TRACE_PANEL_DEPTH),
    sourceSummary:
      variant === 'degraded'
        ? undefined
        : {
            // Matches the chip tooltip's precedence so the two counts never disagree:
            // outcome's wire value, then the result's, then the local attempted count.
            count:
              options.outcomeSourcesChecked ??
              result?.evidence.sourcesChecked ??
              sources.filter((source) => source.attempted).length,
            sources: naming === 'named' ? sources : [],
            tone: 'positive',
          },
    footer: footerFor(variant, reason, fields, options.relativeTime),
  };
}

function fieldRowState(state: EnrichmentFieldState | undefined): TracePanelLoadingRowState {
  return state && state !== EnrichmentFieldState.CHECKING ? 'done' : 'checking';
}

export function mapEnrichmentLoadingPanel(fields: FieldStates): TracePanelLoadingModel {
  const phoneState = fieldRowState(fields?.phone);
  const emailState = fieldRowState(fields?.email);
  const rows: TracePanelLoadingRow[] = [
    { id: 'phone', icon: 'check', headline: 'Phone', state: phoneState },
    { id: 'email', icon: 'check', headline: 'Email', state: emailState },
  ];

  return { subline: 'Verifying contact info in real time', rows };
}
